'use client';

import { useState } from 'react';
import { TelegramProductExtendedDto } from '@/generated';
import { AdvancedMarker, APIProvider, Map } from '@vis.gl/react-google-maps';
import { getProductPlacementLabel } from '@/utils/getProductPlacementLabel';
import { useLocale, useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';

const GMAPS_KEY = process.env.NEXT_PUBLIC_GMAPS_KEY || process.env.GMAPS_KEY || '';

type ProductFullPlacementProps = {
  product: TelegramProductExtendedDto;
};

export function ProductFullPlacement({ product }: ProductFullPlacementProps) {
  const locale = useLocale();
  const t = useTranslations('product');
  const [openedMap, setOpenedMap] = useState(false);

  const locationLabel = getProductPlacementLabel(product, locale) ?? t('placementNotFound');
  const hasExactPlacement = typeof product.placement === 'string' && product.placement.trim().length > 0;
  const hasValidCoords =
    Number.isFinite(product.lat) && Number.isFinite(product.lon) && (product.lat !== 0 || product.lon !== 0);
  const canShowExactLocation = Boolean(product.country?.id) && hasExactPlacement && hasValidCoords;
  const mapCoords = canShowExactLocation ? { lat: product.lat as number, lng: product.lon as number } : null;

  return (
    <>
      <p className='font-normal text-[16px] text-text-main-day leading-[20px] mt-[40px] lg:mt-[24px]'>
        {locationLabel}
      </p>
      {canShowExactLocation ? (
        <>
          <p className='font-normal text-[16px] text-text-main-day leading-[20px] mt-[12px]'>
            {t('address', {
              address: product.placement || '',
            })}
          </p>

          <Button
            variant='link'
            onClick={() => {
              setOpenedMap((val) => {
                return !val;
              });
            }}
            className='h-auto p-0 self-start text-green-day text-[16px] mt-[12px] no-underline hover:no-underline'
          >
            {openedMap ? t('hideMap') : t('showMap')}
          </Button>

          {openedMap && mapCoords ? (
            <div className='w-full lg:mt-[24px] mt-[16px]'>
              <APIProvider language={locale || 'en'} apiKey={GMAPS_KEY}>
                <Map
                  defaultCenter={mapCoords}
                  defaultZoom={15}
                  style={{ width: '100%', height: '270px' }}
                  mapId='VIEW_MAP_ID'
                >
                  <AdvancedMarker position={mapCoords}>
                    {/* eslint-disable-next-line next/no-img-element -- маркер внутри Google Map AdvancedMarker, next/image lazy-loading здесь неприменим */}
                    <img src='/assets/icons/map-pin.svg' width={36} height={46} alt='' />
                  </AdvancedMarker>
                </Map>
              </APIProvider>
            </div>
          ) : null}
        </>
      ) : (
        <></>
      )}
    </>
  );
}
